import { Alert, Button, Collapse, DisplayField, Icon, Spinner } from '@digitools/honest-react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import queryString from 'query-string';
import getProductAttributeCode from 'src/packages/status/utilities/getProductAttributeCode';
import useExperience from 'src/hooks/useExperience';
import useAuthentication from 'src/hooks/useAuthentication';
import getClaimStatusDefinition from 'src/packages/status/utilities/getClaimStatusDefinition';
import checkDate from 'src/packages/status/utilities/checkDate';
import Card from '../../../../../components/Cards/Card';
import { encode } from 'status/utilities/hash';
import useTealium from 'src/hooks/useTealium';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import styles from './CoverageDetails.scss';
import getLeaveStatusDefinition from 'src/packages/status/utilities/getLeaveStatusDefinition';
import { compareByField } from 'src/utils/SortUtils';
import StatusBadge, { BadgeButton, getStatusColor } from 'src/components/Cards/StatusBadge';
import styled from 'styled-components';
import StatusText from 'src/packages/status/constants/StatusText';
import useTranslate from 'src/hooks/useTranslate';
import useStatusDataContext from 'src/packages/status/hooks/useStatusDataContext';
import { RtwContext } from 'src/packages/status/context';
import { Experience } from 'src/types/Experience';
import getDisabilityCause from 'src/packages/status/utilities/getDisabilityCause';
import { PENDING_STATUS_CODES, DENIED_STATUS_CODES } from '../../../constants/StatusCodes';
import { isProd } from 'src/utils/envUtils';
const { FILTER, LINKS, ALERTS, FIELDS, COVERAGE, showLess, showMore } = StatusText;
//TODO: move into some shared place for components
export const StatusMessage = styled('div') `
  color: ${({ theme }) => theme?.palette?.gray}
  font-Style: italic;
  font-size: .9rem;
  margin-left: .5rem;
  margin-right: .5rem;
  margin-top: .75rem;
  margin-bottom: .75rem;
}
`;
const StyledPairedDiv = styled('div') `
@media (min-width: 440px) {
  display: flex;
  flex-direction: row;
  column-gap: .75rem;
}
  @media (max-width: 440px) {
    display: flex;
    flex-direction: row;
    flex: 0 0 100%;
    column-gap: .75rem;
  }
`;
//FIXME: text underline not working here
const LinkIcon = styled(Icon) `
  &:hover {
    text-decoration: none !important;
  }
`;
const isPaidLeaveCoverage = (coverageCode) => !isProd() && !!coverageCode?.includes('PL');
/**
 * Helper function for if rtw is configured for this user's customer & their current experience
 *
 * @param experience
 * @param user
 * @returns
 */
const hasRtwConfigured = (experience, user) => {
    const applicationPermissions = user?.applicationPermissions;
    if (experience === 'Employer') {
        return applicationPermissions?.indexOf('statusER.claimLeaveStatus.displayRtw') > -1;
    }
    else {
        return applicationPermissions?.indexOf('statusEE.claimLeaveStatus.displayRtw') > -1;
    }
};
/**
 * RTW only shows on in progress, approved, pending or pending events - basically aything but denied or closed.
 * RTW only shows for leaves in similar statuses, and only if they are continuous leaves without a coordinated claim number
 * should also just be on the back end and have the event give us a boolean for this
 *
 * @param event
 */
const shouldShowRtwForEvent = (event) => {
    // these make the logic too complicated - but are how statuses are in the backend separated out
    // let docsNetStatusEn = ['In Progress', 'Open', 'Pended'];
    // let docsNetStatusEs = ['En Progreso', 'Abierto', 'Pendiente'];
    // let s1NetStatusEn = ['Pending', 'Approved', 'Pended'];
    // let s1NetStatusEs = ['Pendiente', 'Aprobadoo'];
    // let leaveDocsnetEn = ['Pending', 'Approved'];
    // let leaveDocsnetEs = ['Pendiente', 'Aprobadoo'];
    // combined all into one list of approved statuses
    let statusesToShow = ['In Progress', 'Open', 'Pended', 'Pending', 'Approved', 'En Progreso', 'Abierto', 'Pendiente', 'Aprobadoo']; //Aprobadoo may be type but just in case
    if (statusesToShow.includes(event.status)) {
        if (event.product === 'Leave') {
            // only shows on continuous leaves without a coordinated claim number
            if (event.crdClm === '0' && event.leaveCategory === 'Continuous') {
                return true;
            }
        }
        else {
            // nothing else to check for claims
            return true;
        }
    }
};
const getCoverageName = (coverage, t) => {
    switch (coverage.trim()) {
        case 'STD':
            // case 'STDCP': //pending
            return t(COVERAGE.STD);
        case 'LTD':
            // case 'LTDCP': //pending
            return t(COVERAGE.LTD);
        case 'STAT':
            return t(COVERAGE.STAT);
        case 'PLNY':
        case 'PLVA':
        case 'PLVB':
        case 'PLCT':
        case 'PLCO':
        case 'PLOR':
        case 'PLCA':
        case 'PLDE':
        case 'PLMD':
        case 'PLME':
        case 'PLMN':
        case 'MAPL':
        case 'WAPL':
            return t(COVERAGE.paidLeave);
        // case 'SALCN': // not yet useed
        //   return 'Salary continuation'
        default:
            return coverage.trim();
    }
};
const EVENT_INCREMENT = 3;
const StyledCardLink = styled(Link) `
  display: flex;
  gap: .25rem;
`;
const StyledCardLinkContainer = styled.div `
  display: flex;
  row-gap: .75rem;
  column-gap: 1.5rem;
  flex-wrap: wrap;
`;
const ClaimsAndLeaves = ({ claims, leaves, error }) => {
    const { empNum, from } = useParams();
    const parsed = queryString.parse(location.search);
    const { productAttributeCodes, statusDefinitionList } = useStatusDataContext();
    const { language, experience } = useExperience();
    const { user } = useAuthentication();
    const { t } = useTranslate();
    const { trackEvent } = useTealium();
    //TODO: make sure this works & updates responsivly
    let mobile = window.matchMedia("(max-width: 768px)");
    const [numEventsToDisplay, setNumEventsToDisplay] = useState(EVENT_INCREMENT + (!mobile.matches ? 3 : 0)); // initially show 7 on desktop
    const navigate = useNavigate();
    const [filter, setFilter] = useState('all');
    const [sortBy, setSortBy] = useState({ field: 'receivedDate', ascending: false });
    const [selectedEvent, setSelectedEvent] = useState();
    const focusIndexRef = useRef(null);
    useEffect(() => {
        if (focusIndexRef.current !== null) {
            const container = document.getElementById('claims&leaves-container');
            if (container) {
                const cards = container.querySelectorAll('[data-testid="event"]');
                const cardToFocus = cards[focusIndexRef.current];
                cardToFocus?.focus();
            }
            focusIndexRef.current = null;
        }
    }, [numEventsToDisplay]);
    //@ts-ignore -- this could be removed in favor of session storage only - or vice versa
    const { saveRtwNavigation } = useContext(RtwContext);
    if (!empNum || claims?.length === 0 && leaves?.length === 0) {
        return <Alert type={'info'}>{t(ALERTS.noClaimsOrLeaves)}</Alert>;
    }
    if (error && error.response.status !== 404) {
        return <Alert type={'error'}>{experience === Experience.EMPLOYEE ? t(ALERTS.fetchClaimsErrorEE) : t(ALERTS.fetchClaimsErrorER)}</Alert>;
    }
    if (!user) {
        return <Spinner />;
    }
    /****** FORMATTING CODE -- TODO: THIS SHOULD BE IN THE BACK END!!! *************
    * need to spin up a ticket to do that work, way too much going on here
    * translate this properly - currently formats into english or spanish then other logic here in ui is based on these values
    * should provide UI with display (translated) value and only send codes if they're meant to be displayed
    */
    let formattedData = useMemo(() => {
        let data = [];
        // Format claims
        if ((claims && filter === 'all') || filter === 'claims') {
            claims.forEach(claim => {
                const status = getProductAttributeCode(language, productAttributeCodes, 'Claim', 'Status', claim.clmSttusCde, user.customerInContext?.smallMid);
                const statusDefinition = getClaimStatusDefinition(language, statusDefinitionList, claim.prodType, claim.clmSttusCde, claim.clmClosureReasCde, claim.clmPendReasCde, user.customerInContext?.smallMid);
                const showPaidLeaveFields = isPaidLeaveCoverage(claim.prodType) && !!claim.claimPaidLeaveInfo;
                const formattedClaim = {
                    product: 'Claim',
                    eventId: claim.clmNum,
                    receivedDate: claim.clmRecdDte && claim.clmRecdDte.split(' ')[0],
                    determinationDate: checkDate(claim.clmDtrmDte), // TODO: clean up as part of formatting in back end
                    coverageCode: claim.prodType,
                    coverage: getCoverageName(claim.prodType, t),
                    status,
                    statusCode: claim.clmSttusCde,
                    statusDefinition,
                    empNum,
                    url: 'claims',
                    eventReason: getDisabilityCause(claim.clmDisabCause),
                    eventBeginDate: checkDate(claim.clmDisabDte),
                    eventEndDate: checkDate(claim.disabApprvThruDte),
                    ...(showPaidLeaveFields && {
                        leaveCategory: getProductAttributeCode(language, productAttributeCodes, 'Leave', 'Category', claim.claimPaidLeaveInfo.lveCatg),
                        leaveReason: getProductAttributeCode(language, productAttributeCodes, 'Leave', 'Reason', claim.claimPaidLeaveInfo.lveReas),
                    }),
                };
                data.push(formattedClaim);
            });
            /*** FOR TESTING & DEMO PURPOSES - DELETE LATER */
            // const dummyClaim = {
            //   product: 'Claim',
            //   event: 12345678,
            //   receivedDate: '06/06/2024',
            //   determinationDate: '06/26/2024',
            //   coverageCode: 'STAT',
            //   coverage: getCoverageName('STAT'),
            //   status: 'Approved',
            //   statusDefinition: 'Your claim has been processed and approved.',
            //   uploadDocuments: '',
            //   empNum,
            //   url: 'claims',
            //   // ...params,
            // };
            // data.push(dummyClaim)
        }
        // Format Leaves
        if ((leaves && filter === 'all') || filter === 'leaves') {
            leaves.forEach(leave => {
                const status = getProductAttributeCode(language, productAttributeCodes, 'Leave', 'Status', leave.lveSttusCde);
                const statusDefinition = getLeaveStatusDefinition(language, statusDefinitionList, leave.lveType, leave.lveSttusCde, leave.lveSttusReasCde);
                const formattedLeave = {
                    product: 'Leave',
                    eventId: leave.lveNum,
                    receivedDate: leave.lveRecdDte && leave.lveRecdDte.split(' ')[0],
                    determinationDate: checkDate(leave.lveDtrmDte),
                    coverage: t(COVERAGE.leave),
                    statusCode: leave.lveSttusCde,
                    status,
                    statusDefinition,
                    uploadDocuments: '',
                    empNum,
                    url: 'leaves',
                    crdClm: leave.coordClmNum,
                    leaveCategory: getProductAttributeCode(language, productAttributeCodes, 'Leave', 'Category', leave.lveCatg),
                    eventReason: getProductAttributeCode(language, productAttributeCodes, 'Leave', 'Reason', leave.lveReas),
                    eventBeginDate: checkDate(leave.lveBeginDte),
                    eventEndDate: checkDate(leave.lveEndDte)
                };
                data.push(formattedLeave);
            });
            /*** FOR TESTING & DEMO PURPOSES - DELETE LATER */
            // const dummyLeave = {
            //   product: 'Leave',
            //   event: 23456890,
            //   receivedDate: '07/14/2023',
            //   determinationDate: '07/20/2023',
            //   coverageCode: 'PLNY',
            //   coverage: 'Leave',
            //   status: 'Approved',
            //   statusDefinition: 'Your claim has been processed and approved.',
            //   uploadDocuments: '',
            //   empNum,
            //   url: 'claims',
            //   // ...params,
            // };
            // data.push(dummyClaim)
        }
        return data;
    }, [leaves, claims, filter]);
    const getEventUrl = (event) => {
        let to = `/status/${experience}/${event.empNum}/${event.url}/${encode(event.eventId)}/details`;
        if (experience === 'Employer') {
            to = `${to}?search=${parsed.search}&searchCrit=${parsed.searchCrit}&from=coveragePage`;
        }
        else if (parsed.from === 'teamView') {
            to = `${to}?from=teamView`;
        }
        else if (parsed.singleResult) {
            to = `${to}?singleResult=true&search=${parsed.search}&searchCrit=${parsed.searchCrit}&from=coveragePage`;
        }
        return to;
    };
    const handleCardClick = (event, e) => {
        const to = getEventUrl(event);
        const statusNav = window.sessionStorage.getItem('statusNav');
        let eventType = event.eventId;
        if (statusNav === 'rtw') {
            eventType = StatusText.LINKS.returnToWork.english;
        }
        navigate(to);
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
            event_name: 'event_no',
            event_results: event.product.toLowerCase(),
            event_version: 'details',
        });
    };
    /**
     *  Flips sort if you click the already active sort field, otherwise sets it to ascending
     * @param newSort
     */
    const changeSort = (newSort) => {
        setSortBy({ field: newSort, ascending: sortBy.field === newSort ? !sortBy.ascending : true });
    };
    const isSTDLTD = (eventProdType) => {
        if (eventProdType.trim() === "STD" || eventProdType.trim() === 'Short Term Disability' || eventProdType.trim() === "LTD" || eventProdType.trim() === 'Long Term Disability') {
            return true;
        }
        return false;
    };
    //Denied, Inprogress, or Pending
    const isDEorIPorPE = (status) => {
        if (DENIED_STATUS_CODES.includes(status) || PENDING_STATUS_CODES.includes(status)) {
            return true;
        }
        return false;
    };
    let sortedData = [];
    if (formattedData && formattedData?.length > 0) {
        sortedData = formattedData.sort(compareByField(sortBy.field, sortBy.ascending));
    }
    const Heading = (coverage, coverageCode) => {
        return (<div data-private={true}>{`${coverage} ${coverageCode && !user?.customerInContext?.smallMid ? `(${coverageCode.trim()})` : ''}`}</div>);
    };
    return (<>
      <Collapse headerElemType={'h2'} title={t(FILTER.sortAndFilter)}>
        {/* TODO: export these styles/inlines into styled components or classes -- entire filter box should be its own component(s) - props:  filter, setFilter */}
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', padding: '0' }}>
          <div className={styles.allTableFilterGroup} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{
            position: 'absolute',
            backgroundColor: 'white',
            marginTop: '-1.33rem',
            padding: '0 .25rem 0 .25rem',
            fontWeight: 'bold',
        }}>
              {t(FILTER.filter)}
            </span>
            <BadgeButton backgroundColor={filter === 'all' ? 'secondaryDark' : 'gray'} onClick={() => setFilter('all')} aria-label={`${t(FILTER.all)}${filter === 'all' ? `: ${t(FILTER.selected)}` : `: ${t(FILTER.notSelected)}`}`}>
              {t(FILTER.all)}
            </BadgeButton>
            <BadgeButton backgroundColor={filter === 'claims' ? 'secondaryDark' : 'gray'} onClick={() => setFilter('claims')} aria-label={`${t(FILTER.claims)}${filter === 'claims' ? `: ${t(FILTER.selected)}` : `: ${t(FILTER.notSelected)}`}`}>
              {t(FILTER.claims)}
            </BadgeButton>
            <BadgeButton backgroundColor={filter === 'leaves' ? 'secondaryDark' : 'gray'} onClick={() => setFilter('leaves')} aria-label={`${t(FILTER.leaves)}${filter === 'leaves' ? `: ${t(FILTER.selected)}` : `: ${t(FILTER.notSelected)}`}`}>
              {t(FILTER.leaves)}
            </BadgeButton>
          </div>
          {/* TODO: export these styles/inlines into styled components or classes  -- entire sort box should be its own component(s) - props:  sortBy object, setSortBy (include changeSort in it) */}
          <div className={styles.allTableFilterGroup} style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            <span style={{
            position: 'absolute',
            backgroundColor: 'white',
            marginTop: '-1.33rem',
            padding: '0 .25rem 0 .25rem',
            fontWeight: 'bold',
        }}>
              {t(FILTER.sort)}
            </span>
            <BadgeButton onClick={() => changeSort('eventId')} backgroundColor={sortBy.field === 'eventId' ? 'secondaryDark' : 'gray'} aria-label={`${t(FILTER.eventNumber)} ${sortBy.field === 'eventId' ? (sortBy.ascending ? 'sorted ascending' : 'sorted descending') : 'not sorted'}`}>
              {t(FILTER.eventNumber)}{' '}
              <Icon color='white' style={{ fontSize: '.75rem' }} name={sortBy.ascending ? 'long-arrow-up-regular' : 'long-arrow-down-regular'} size='small'/>
            </BadgeButton>
            <BadgeButton onClick={() => changeSort('coverage')} backgroundColor={sortBy.field === 'coverage' ? 'secondaryDark' : 'gray'} aria-label={`${t(FILTER.product)} ${sortBy.field === 'coverage' ? (sortBy.ascending ? 'sorted ascending' : 'sorted descending') : 'not sorted'}`}>
              {t(FILTER.product)}
              <Icon color='white' style={{ fontSize: '.75rem' }} className='ml-2' name={sortBy.ascending ? 'long-arrow-up-regular' : 'long-arrow-down-regular'}/>
            </BadgeButton>
            <BadgeButton onClick={() => changeSort('receivedDate')} backgroundColor={sortBy.field === 'receivedDate' ? 'secondaryDark' : 'gray'} aria-label={`${t(FILTER.recDate)} ${sortBy.field === 'receivedDate' ? (sortBy.ascending ? 'sorted ascending' : 'sorted descending') : 'not sorted'}`}>
              {t(FILTER.recDate)}
              <Icon color='white' style={{ fontSize: '.75rem' }} className='ml-2' name={sortBy.ascending ? 'long-arrow-up-regular' : 'long-arrow-down-regular'}/>
            </BadgeButton>
            <BadgeButton onClick={() => changeSort('status')} backgroundColor={sortBy.field === 'status' ? 'secondaryDark' : 'gray'} aria-label={`${t(FILTER.status)} ${sortBy.field === 'status' ? (sortBy.ascending ? 'sorted ascending' : 'sorted descending') : 'not sorted'}`}>
              {t(FILTER.status)}
              <Icon className='ml-2' color='white' style={{ fontSize: '.75rem' }} name={sortBy.ascending ? 'long-arrow-up-regular' : 'long-arrow-down-regular'}/>
            </BadgeButton>
          </div>
        </div>
      </Collapse>
      <br />
      <div id='claims&leaves-container' style={{ width: '100%', }}>
          {sortedData.map((item, i) => {
            if (i >= numEventsToDisplay) {
                return <></>;
            }
            const isPLNonProd = isPaidLeaveCoverage(item.coverageCode);
            return (<>
                  <Card tabIndex={0} data-testid={'event'} key={item.eventId} heading={Heading(item.coverage, item.coverageCode)} secondaryHeading={<StatusBadge status={item.status}>{item.status}</StatusBadge>} selectable={true} selected={item.eventId === selectedEvent} onCardClick={(e) => handleCardClick(item, e)} fontSize='large' small={true} borderColor={getStatusColor(item.status)} cardLinks={<>
                        <StyledCardLinkContainer>
                          {/* TODO: accessibility - do these links need to have the url they'll send you to? (and not rely on wrapping Link onClick behavior) */}
                          <StyledCardLink to={getEventUrl(item)} aria-label={`${t(LINKS.uploadDocuments)}: ${t(FILTER.eventNumber)} ${item.eventId}`} onClick={() => window.sessionStorage.setItem('statusNav', 'upload')}>
                            {t(LINKS.uploadDocuments)} <LinkIcon color='secondaryDark' name='upload'/>
                          </StyledCardLink>
                          {hasRtwConfigured(experience, user) && shouldShowRtwForEvent(item) && (<StyledCardLink to={getEventUrl(item)} aria-label={`${t(LINKS.returnToWork)}: ${t(FILTER.eventNumber)} ${item.eventId}`} onClick={() => {
                            window.sessionStorage.setItem('statusNav', 'rtw');
                            saveRtwNavigation(true);
                        }}>
                              {t(LINKS.returnToWork)}
                              <LinkIcon color='secondaryDark' name='calendar'/>
                            </StyledCardLink>)}
                          <StyledCardLink to={getEventUrl(item)} aria-label={`${t(LINKS.viewDetails)}: ${t(FILTER.eventNumber)} ${item.eventId}`}>
                            {t(LINKS.viewDetails)}
                            <LinkIcon className={styles.iconClass} color='secondaryDark' style={{ fontSize: '.75rem', marginTop: '.25rem' }} name='long-arrow-right-regular'/>
                          </StyledCardLink>
                        </StyledCardLinkContainer>
                      </>}>
                    {item.statusDefinition && <StatusMessage><Icon size='sm' name='important-message' color='grayDark' className='ml-2 mr-2'/>{item.statusDefinition}</StatusMessage>}
                    <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: '.5rem', columnGap: '.75rem', }}>
                      <DisplayField label={t(FIELDS.eventNumberText)} value={item.eventId} style={{ minWidth: '5rem', flexGrow: '1', }}/>
                      <DisplayField label={t(FIELDS.receivedDateText)} value={item.receivedDate} style={{ minWidth: '5rem', flexGrow: '1', }}/>
                      {item.product === "Leave" && <>
                        <DisplayField data-private={true} label={t(FIELDS.leaveReason)} value={item.eventReason} style={{ minWidth: '5rem', flexGrow: '1', }}/>
                        <StyledPairedDiv>
                          <DisplayField label={t(FIELDS.leaveBeginDate)} value={isDEorIPorPE(item.statusCode) ? "" : item.eventBeginDate} style={{ minWidth: '5rem', flexGrow: '1', }}/>
                          <DisplayField label={t(FIELDS.leaveEndDate)} value={isDEorIPorPE(item.statusCode) ? "" : item.eventEndDate} style={{ minWidth: '5rem', flexGrow: item.leaveCategory ? '1' : '10' }}/>
                        </StyledPairedDiv>
                        {item.leaveCategory && <DisplayField data-private={true} label={t(FIELDS.leaveCategoryText)} value={item.leaveCategory} style={{ flexGrow: '8', }}/>}
                      </>}
                      {item.product === "Claim" && <>
                      {experience === Experience.EMPLOYER && !isPLNonProd && <DisplayField data-private={true} label={t(FIELDS.disabilityCause)} value={item.eventReason} style={{ minWidth: '5rem', flexGrow: '1', }}/>}
                        <DisplayField label={t(FIELDS.dateOfDisability)} value={item.eventBeginDate} style={{ minWidth: '5rem', flexGrow: isPLNonProd || !isDEorIPorPE(item.statusCode) ? '1' : '10' }}/>
                        {isPLNonProd &&
                        <>
                            <DisplayField label={t(FIELDS.leaveReason)} value={item.leaveReason} style={{ minWidth: '5rem', flexGrow: '1', }}/>
                            <DisplayField data-private={true} label={t(FIELDS.leaveCategoryText)} value={item.leaveCategory} style={{ minWidth: '5rem', flexGrow: isDEorIPorPE(item.statusCode) ? '10' : '1' }}/>
                          </>}
                        {!isDEorIPorPE(item.statusCode) && <DisplayField label={((isSTDLTD(item.coverageCode) ? "*" : "") + (user.customerInContext?.smallMid ? t(FIELDS.approvedToDate) : t(FIELDS.approvedThroughDate)))} value={item.eventEndDate} style={{ minWidth: '5rem', flexGrow: '10' }}/>}
                      </>}
                    </div>
                    {(item.product === "Claim" && !isDEorIPorPE(item.statusCode) && isSTDLTD(item.coverageCode)) &&
                    <StatusMessage><Icon size='sm' name='important-message' color='grayDark' className='ml-2 mr-2'/><strong>*</strong>{t(FIELDS.dateDisclaimer)}</StatusMessage>}
                  </Card>
              </>);
        })}
          <div id='showMoreContainer' style={{ display: 'flex', justifyContent: 'center' }}>
          {numEventsToDisplay < sortedData.length && (<Button buttonType='text' onClick={() => {
                focusIndexRef.current = numEventsToDisplay;
                setNumEventsToDisplay(numEventsToDisplay + (EVENT_INCREMENT + (!mobile.matches ? 3 : 0)));
            }}>
              {t(showMore)}
            </Button>)}
          {numEventsToDisplay >= sortedData.length && numEventsToDisplay > (EVENT_INCREMENT + (!mobile.matches ? 3 : 0)) && (<Button buttonType='text' onClick={() => setNumEventsToDisplay(numEventsToDisplay - (EVENT_INCREMENT + (!mobile.matches ? 3 : 0)))}>
              {t(showLess)}
            </Button>)}
          </div>
        </div>
      <hr />
    </>);
};
export default ClaimsAndLeaves;
